import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery } from "@tanstack/react-query"
import {
  Badge,
  Center,
  Container,
  Flex,
  Heading,
  Spinner,
  Text,
} from "@chakra-ui/react"
import { FiArrowLeft } from "react-icons/fi"
import { ProviderUsers } from "@/client"
import EditProviderUser from "@/components/ProviderUsers/EditProviderUser"

interface ProviderUsersEditSearch {
  providerUserId: string
}

export const Route = createFileRoute("/_layout/provider/providerusers/edit")({
  component: ProviderUsersEdit,
  validateSearch: (search: Record<string, unknown>): ProviderUsersEditSearch => {
    return {
      providerUserId: (search.providerUserId as string) || '',
    }
  },
})

function ProviderUsersEdit() {
  const navigate = useNavigate()
  const storedProviderId = sessionStorage.getItem("selectedProviderId")
  const { providerUserId } = Route.useSearch()

  const { data: providerUserResponse, isLoading, error } = useQuery({
    queryKey: ["providerUser", providerUserId],
    queryFn: () => ProviderUsers.readProviderUsers({
      query: { providerId: storedProviderId || '', providerUserId },
    }),
    enabled: !!providerUserId && !!storedProviderId,
  })

  const providerUser = providerUserResponse?.data?.[0] || null

  if (!providerUserId) {
    return (
      <Container maxW="full" pt={12}>
        <Text color="red.500">No provider user ID provided.</Text>
      </Container>
    )
  }

  if (isLoading) {
    return (
      <Container maxW="full" pt={12}>
        <Center mt={16}><Spinner size="lg" /></Center>
      </Container>
    )
  }

  if (error || !providerUser) {
    return (
      <Container maxW="full" pt={12}>
        <Text color="red.500">Failed to load provider user.</Text>
      </Container>
    )
  }

  return (
    <Container maxW="full" pt={8} pb={12}>
      {/* Back link */}
      <button
        onClick={() => navigate({ to: "/provider/providerusers" })}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: "0.8rem",
          color: "#718096",
          marginBottom: 16,
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: 0,
        }}
      >
        <FiArrowLeft size={13} /> Provider Users
      </button>

      {/* User identity header */}
      <Flex align="baseline" gap={3} mb={6}>
        <Heading size="xl">{providerUser.full_name || "Unnamed User"}</Heading>
        <Text fontSize="md" color="gray.500">{providerUser.email}</Text>
        {providerUser.isProviderAdmin && (
          <Badge colorPalette="purple" size="sm">Provider Admin</Badge>
        )}
      </Flex>

      <EditProviderUser
        providerUser={providerUser}
        onCancel={() => navigate({ to: "/provider/providerusers" })}
      />
    </Container>
  )
}
